import { useEffect, useMemo, useState } from 'react';
import type {
  CallTreatment,
  CustomFieldDef,
  CustomFieldType,
  SmartList,
  TenantSettings
} from '@personical/cc-shared-types';
import { CALL_TREATMENTS } from '@personical/cc-shared-types';
import { useAuth } from '../auth.js';
import { useApi } from '../components/api-context.js';
import { useTheme, type ThemePreference } from '../theme.js';

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'Match system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
];

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' }
];

const COUNTRIES: { code: string; name: string }[] = [
  { code: 'GB', name: 'United Kingdom (+44)' },
  { code: 'IE', name: 'Ireland (+353)' },
  { code: 'US', name: 'United States (+1)' },
  { code: 'CA', name: 'Canada (+1)' },
  { code: 'AU', name: 'Australia (+61)' },
  { code: 'NZ', name: 'New Zealand (+64)' },
  { code: 'DE', name: 'Germany (+49)' },
  { code: 'FR', name: 'France (+33)' },
  { code: 'ES', name: 'Spain (+34)' },
  { code: 'IT', name: 'Italy (+39)' },
  { code: 'NL', name: 'Netherlands (+31)' },
  { code: 'BE', name: 'Belgium (+32)' },
  { code: 'CH', name: 'Switzerland (+41)' },
  { code: 'AT', name: 'Austria (+43)' },
  { code: 'SE', name: 'Sweden (+46)' },
  { code: 'NO', name: 'Norway (+47)' },
  { code: 'DK', name: 'Denmark (+45)' },
  { code: 'FI', name: 'Finland (+358)' },
  { code: 'PL', name: 'Poland (+48)' },
  { code: 'PT', name: 'Portugal (+351)' },
  { code: 'IN', name: 'India (+91)' },
  { code: 'JP', name: 'Japan (+81)' },
  { code: 'SG', name: 'Singapore (+65)' },
  { code: 'AE', name: 'United Arab Emirates (+971)' },
  { code: 'ZA', name: 'South Africa (+27)' }
];

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function SettingsPage() {
  const api = useApi();
  const { signOut, state } = useAuth();
  const { preference, resolved, setPreference } = useTheme();
  const account = state.kind === 'authenticated' ? state.user : null;
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [country, setCountry] = useState('GB');
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingCountry, setSavingCountry] = useState(false);
  const [savedCountry, setSavedCountry] = useState(false);
  const [renorming, setRenorming] = useState(false);
  const [renormResult, setRenormResult] = useState<string | null>(null);

  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [savingFields, setSavingFields] = useState(false);
  const [savedFields, setSavedFields] = useState(false);

  const [smartLists, setSmartLists] = useState<SmartList[]>([]);
  const [savingLists, setSavingLists] = useState(false);
  const [savedLists, setSavedLists] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    api
      .getSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setCountry(s.defaultCountry);
        setCustomFields(s.customFields ?? []);
        setSmartLists(s.smartLists ?? []);
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function saveCountry() {
    setSavingCountry(true);
    setError(null);
    setSavedCountry(false);
    try {
      const updated = await api.updateSettings({ defaultCountry: country });
      setSettings(updated);
      setSavedCountry(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingCountry(false);
    }
  }

  async function renormalise() {
    if (
      !confirm(
        "Re-parse every contact's phone numbers using the current default country? Older entries will pick up their E.164 form."
      )
    ) {
      return;
    }
    setRenorming(true);
    setRenormResult(null);
    setError(null);
    try {
      const r = await api.renormaliseAll();
      setRenormResult(`Updated ${r.updated} of ${r.total} contacts (${r.skipped} unchanged).`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRenorming(false);
    }
  }

  async function saveCustomFields() {
    setSavingFields(true);
    setError(null);
    setSavedFields(false);
    try {
      const updated = await api.updateSettings({ customFields });
      setSettings(updated);
      setCustomFields(updated.customFields ?? []);
      setSavedFields(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingFields(false);
    }
  }

  async function saveSmartLists() {
    setSavingLists(true);
    setError(null);
    setSavedLists(false);
    try {
      const updated = await api.updateSettings({ smartLists });
      setSettings(updated);
      setSmartLists(updated.smartLists ?? []);
      setSavedLists(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingLists(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-2xl font-semibold">Settings</h2>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">
          {error}
        </div>
      )}

      <section className="surface rounded-2xl shadow p-6 space-y-3">
        <header>
          <h3 className="font-semibold">Appearance</h3>
          <p className="text-sm text-mid mt-1">
            Currently rendering in <span className="font-medium">{resolved}</span> mode.
          </p>
        </header>
        <div className="flex flex-wrap gap-2">
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPreference(opt.value)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                preference === opt.value
                  ? 'border-slate-900 dark:border-slate-100 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'border-input hover:surface-muted'
              }`}
              aria-pressed={preference === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="surface rounded-2xl shadow p-6 space-y-4">
        <header>
          <h3 className="font-semibold">Default country for phone numbers</h3>
          <p className="text-sm text-mid mt-1">
            When someone enters a phone number without a country code, we use this country
            to normalise it to international (E.164) format. Inbound call lookups expect
            E.164, so getting this right means callers are matched to your contacts.
          </p>
        </header>
        {loadError ? (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">
            <p className="font-medium">Couldn't load tenant settings.</p>
            <p className="mt-1">{loadError}</p>
            <p className="mt-1 text-xs">
              Common causes: Cosmos data-plane RBAC still propagating after a fresh
              deploy (retry in ~60s); the <code>tenant-settings</code> container missing
              (re-run the deploy workflow); or the func is offline.
            </p>
          </div>
        ) : !settings ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <>
            <label className="block">
              <span className="text-sm font-medium text-mid">Country</span>
              <select
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setSavedCountry(false);
                }}
                className="select mt-1 w-full"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={saveCountry}
                disabled={savingCountry || country === settings.defaultCountry}
                className="btn-primary px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {savingCountry ? 'Saving…' : 'Save'}
              </button>
              {savedCountry && (
                <span className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</span>
              )}
            </div>
            <p className="text-xs text-muted pt-2 border-t border-divider">
              Last updated {new Date(settings.updatedAt).toLocaleString()}
            </p>
          </>
        )}
      </section>

      <CustomFieldsEditor
        fields={customFields}
        onChange={(next) => {
          setCustomFields(next);
          setSavedFields(false);
        }}
        onSave={saveCustomFields}
        saving={savingFields}
        saved={savedFields}
      />

      <SmartListsEditor
        lists={smartLists}
        onChange={(next) => {
          setSmartLists(next);
          setSavedLists(false);
        }}
        onSave={saveSmartLists}
        saving={savingLists}
        saved={savedLists}
      />

      <section className="surface rounded-2xl shadow p-6 space-y-3">
        <header>
          <h3 className="font-semibold">Re-normalise existing contacts</h3>
          <p className="text-sm text-mid mt-1">
            New contacts are normalised automatically. Existing contacts only pick up
            their E.164 form on their next edit. Click below to do them all in one pass —
            useful right after changing the default country.
          </p>
        </header>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={renormalise}
            disabled={renorming}
            className="btn-secondary px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            {renorming ? 'Re-normalising…' : 'Re-normalise all contacts'}
          </button>
          {renormResult && <span className="text-sm text-mid">{renormResult}</span>}
        </div>
      </section>

      <section className="surface rounded-2xl shadow p-6 space-y-3">
        <header>
          <h3 className="font-semibold">Account</h3>
          {account && (
            <p className="text-sm text-mid mt-1">
              Signed in as <span className="font-medium">{account.name}</span>
              {account.email ? ` · ${account.email}` : ''}.
            </p>
          )}
        </header>
        <button
          type="button"
          onClick={signOut}
          className="btn-secondary px-4 py-2 text-sm font-medium"
        >
          Sign out
        </button>
      </section>
    </div>
  );
}

interface FieldsEditorProps {
  fields: CustomFieldDef[];
  onChange(next: CustomFieldDef[]): void;
  onSave(): void | Promise<void>;
  saving: boolean;
  saved: boolean;
}

function CustomFieldsEditor({ fields, onChange, onSave, saving, saved }: FieldsEditorProps) {
  function update(index: number, patch: Partial<CustomFieldDef>) {
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }
  function remove(index: number) {
    onChange(fields.filter((_, i) => i !== index));
  }
  function add() {
    const id = `field_${fields.length + 1}`;
    onChange([...fields, { id, label: 'New field', type: 'text' }]);
  }
  const dupIds = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    for (const f of fields) {
      if (seen.has(f.id)) dups.add(f.id);
      seen.add(f.id);
    }
    return dups;
  }, [fields]);

  return (
    <section className="surface rounded-2xl shadow p-6 space-y-4">
      <header>
        <h3 className="font-semibold">Custom fields</h3>
        <p className="text-sm text-mid mt-1">
          Tenant-wide custom fields appear on every contact form. Values are stored as
          strings; the form picks the input type. Use lowercase letters, numbers, and
          hyphens for the id.
        </p>
      </header>

      {fields.length === 0 ? (
        <p className="text-sm text-muted">No custom fields yet.</p>
      ) : (
        <ul className="space-y-3">
          {fields.map((f, i) => (
            <li
              key={i}
              className="rounded-lg border border-divider p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2 items-start"
            >
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-faint">Label</span>
                <input
                  type="text"
                  value={f.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    const id = f.id || slugify(label);
                    update(i, { label, id });
                  }}
                  className="input w-full mt-1"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-faint">Id</span>
                <input
                  type="text"
                  value={f.id}
                  onChange={(e) => update(i, { id: slugify(e.target.value) })}
                  className={`input w-full mt-1 ${dupIds.has(f.id) ? 'border-red-500 dark:border-red-400' : ''}`}
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-faint">Type</span>
                <select
                  value={f.type}
                  onChange={(e) => update(i, { type: e.target.value as CustomFieldType })}
                  className="select w-full mt-1"
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center justify-between gap-2 md:flex-col md:items-end">
                <label className="text-xs flex items-center gap-1 text-mid">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => update(i, { required: e.target.checked })}
                  />
                  Required
                </label>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-xs text-red-700 dark:text-red-400 hover:underline"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={add} className="btn-secondary px-3 py-2 text-sm font-medium">
          + Add field
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || dupIds.size > 0}
          className="btn-primary px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save fields'}
        </button>
        {saved && (
          <span className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</span>
        )}
        {dupIds.size > 0 && (
          <span className="text-sm text-red-700 dark:text-red-400">
            Duplicate id(s): {[...dupIds].join(', ')}
          </span>
        )}
      </div>
    </section>
  );
}

interface SmartListsEditorProps {
  lists: SmartList[];
  onChange(next: SmartList[]): void;
  onSave(): void | Promise<void>;
  saving: boolean;
  saved: boolean;
}

function SmartListsEditor({ lists, onChange, onSave, saving, saved }: SmartListsEditorProps) {
  function update(index: number, patch: Partial<SmartList>) {
    onChange(lists.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  function updateFilter(index: number, patch: Partial<SmartList['filter']>) {
    onChange(
      lists.map((l, i) =>
        i === index ? { ...l, filter: { ...l.filter, ...patch } } : l
      )
    );
  }
  function remove(index: number) {
    onChange(lists.filter((_, i) => i !== index));
  }
  function add() {
    const id = `list_${lists.length + 1}`;
    onChange([...lists, { id, label: 'New list', filter: {} }]);
  }

  return (
    <section className="surface rounded-2xl shadow p-6 space-y-4">
      <header>
        <h3 className="font-semibold">Smart lists</h3>
        <p className="text-sm text-mid mt-1">
          Saved filter combinations that appear as quick chips on the Contacts page —
          shared across the tenant.
        </p>
      </header>

      {lists.length === 0 ? (
        <p className="text-sm text-muted">No smart lists yet.</p>
      ) : (
        <ul className="space-y-3">
          {lists.map((l, i) => (
            <li key={i} className="rounded-lg border border-divider p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_72px] gap-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Label</span>
                  <input
                    type="text"
                    value={l.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      const id = l.id || slugify(label);
                      update(i, { label, id });
                    }}
                    className="input w-full mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Id</span>
                  <input
                    type="text"
                    value={l.id}
                    onChange={(e) => update(i, { id: slugify(e.target.value) })}
                    className="input w-full mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Icon</span>
                  <input
                    type="text"
                    value={l.icon ?? ''}
                    onChange={(e) => update(i, { icon: e.target.value || undefined })}
                    placeholder="★"
                    className="input w-full mt-1"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Search</span>
                  <input
                    type="text"
                    value={l.filter.query ?? ''}
                    onChange={(e) =>
                      updateFilter(i, { query: e.target.value || undefined })
                    }
                    placeholder="optional"
                    className="input w-full mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Tag</span>
                  <input
                    type="text"
                    value={l.filter.tag ?? ''}
                    onChange={(e) =>
                      updateFilter(i, { tag: e.target.value || undefined })
                    }
                    placeholder="optional"
                    className="input w-full mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-faint">Treatment</span>
                  <select
                    value={l.filter.callTreatment ?? ''}
                    onChange={(e) =>
                      updateFilter(i, {
                        callTreatment: (e.target.value || undefined) as CallTreatment | undefined
                      })
                    }
                    className="select w-full mt-1"
                  >
                    <option value="">Any</option>
                    {CALL_TREATMENTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs flex items-center gap-1 text-mid">
                  <input
                    type="checkbox"
                    checked={!!l.filter.favouritesOnly}
                    onChange={(e) =>
                      updateFilter(i, { favouritesOnly: e.target.checked })
                    }
                  />
                  Favourites only
                </label>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-xs text-red-700 dark:text-red-400 hover:underline"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={add} className="btn-secondary px-3 py-2 text-sm font-medium">
          + Add smart list
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="btn-primary px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save lists'}
        </button>
        {saved && (
          <span className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</span>
        )}
      </div>
    </section>
  );
}
